import { useEffect, useState } from "react";
import { Menu, X, Phone } from "lucide-react";
import { BUSINESS, NAV_LINKS } from "../config/business";
import { useSectionNav } from "../hooks/useSectionNav";

export const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const go = useSectionNav();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const goAndClose = (id) => {
    setOpen(false);
    go(id);
  };

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 border-b transition-colors duration-300 ${
        scrolled
          ? "border-white/10 bg-[#0b0c0e]/90 backdrop-blur-md"
          : "border-transparent bg-transparent"
      }`}
    >
      <div className="mx-auto flex h-[76px] max-w-7xl items-center justify-between px-5 lg:px-8">
        <button
          data-testid="nav-logo"
          onClick={() => goAndClose("home")}
          aria-label="Auto Trust Centre — home"
        >
          <img
            src="/images/logo.png"
            alt="Auto Trust Centre Ltd. — official logo"
            className="h-11 w-auto rounded-[3px] sm:h-12"
          />
        </button>

        <nav className="hidden items-center gap-8 lg:flex" aria-label="Primary">
          {NAV_LINKS.map((l) => (
            <button
              key={l.id}
              data-testid={`nav-link-${l.id}`}
              onClick={() => goAndClose(l.id)}
              className="font-display text-xs font-bold uppercase tracking-[0.18em] text-[#c9ccd2] transition-colors hover:text-white"
            >
              {l.label}
            </button>
          ))}
        </nav>

        <div className="hidden items-center gap-4 lg:flex">
          <a
            data-testid="nav-phone"
            href={BUSINESS.phoneHref}
            className="flex items-center gap-2 text-sm font-semibold text-[#c9ccd2] transition-colors hover:text-white"
          >
            <Phone className="h-4 w-4 text-[#00AEEF]" />
            {BUSINESS.phone}
          </a>
          <button
            data-testid="nav-book-button"
            onClick={() => goAndClose("appointment")}
            className="btn-primary !px-5 !py-2.5 !text-xs"
          >
            Book an Appointment
          </button>
        </div>

        <button
          data-testid="nav-menu-toggle"
          className="flex h-11 w-11 items-center justify-center border border-white/15 text-white lg:hidden"
          onClick={() => setOpen((v) => !v)}
          aria-label={open ? "Close menu" : "Open menu"}
          aria-expanded={open}
        >
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>

      {open && (
        <div
          data-testid="nav-mobile-menu"
          className="border-t border-white/10 bg-[#0b0c0e]/95 backdrop-blur-md lg:hidden"
        >
          <nav className="flex flex-col px-5 py-4" aria-label="Mobile">
            {NAV_LINKS.map((l) => (
              <button
                key={l.id}
                data-testid={`nav-mobile-link-${l.id}`}
                onClick={() => goAndClose(l.id)}
                className="font-display border-b border-white/5 py-4 text-left text-base font-bold uppercase tracking-[0.15em] text-[#e6e7ea]"
              >
                {l.label}
              </button>
            ))}
            <a
              data-testid="nav-mobile-phone"
              href={BUSINESS.phoneHref}
              className="flex items-center gap-2 py-4 font-semibold text-[#c9ccd2]"
            >
              <Phone className="h-4 w-4 text-[#00AEEF]" />
              {BUSINESS.phone}
            </a>
            <button
              data-testid="nav-mobile-book-button"
              onClick={() => goAndClose("appointment")}
              className="btn-primary mb-3 mt-1 w-full"
            >
              Book an Appointment
            </button>
          </nav>
        </div>
      )}
    </header>
  );
};
